/**
 * Shared stagger state for blog preview/playground components.
 *
 * All ScadPreview and ScadPlayground instances on a page share the same
 * mount-order counter so each component can stagger its entrance animation
 * by `slot * STAGGER_MS` milliseconds.
 *
 * Usage:
 *  - Call `registerInstance()` from a useEffect with no deps to track the
 *    component lifecycle. The returned cleanup is called on unmount.
 *  - Call `claimSlot()` once per instance (e.g. inside the same useEffect)
 *    to get a monotonically increasing integer that determines animation delay.
 *  - When all instances have unmounted (navigation away), the counter resets
 *    so the next page load starts fresh.
 */

/** Delay between successive component entrance animations, in milliseconds. */
export const STAGGER_MS = 800;

let mountOrder = 0;
let instanceCount = 0;

/**
 * Returns a monotonically increasing slot index (0, 1, 2, …).
 * Multiply by `STAGGER_MS` to get the animation delay for this instance.
 */
export function claimSlot(): number {
  return mountOrder++;
}

/**
 * Register a mounted instance and return a cleanup function for useEffect.
 * When the last instance unmounts the mount-order counter resets to 0 so the
 * next page load begins fresh.
 */
export function registerInstance(): () => void {
  if (instanceCount === 0) {
    // First instance of a new lifecycle — reset the slot counter.
    mountOrder = 0;
  }
  instanceCount++;
  return () => {
    instanceCount--;
    if (instanceCount === 0) {
      mountOrder = 0;
    }
  };
}

// ── Legacy queue-based API (kept for backward compatibility) ─────────────────

/** How many playgrounds may be initialising concurrently (1 = sequential). */
const MAX_CONCURRENT_INITS = 1;

let activeInits = 0;
const waitQueue: Array<() => void> = [];

/**
 * Called when a playground enters the viewport.
 * Resolves immediately if no other playground is currently initialising,
 * otherwise waits in a FIFO queue until a slot becomes free.
 */
export function waitForSlot(): Promise<void> {
  if (activeInits < MAX_CONCURRENT_INITS) {
    activeInits++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    waitQueue.push(() => {
      activeInits++;
      resolve();
    });
  });
}

/**
 * Called when a playground has finished its init phase (first compile result).
 * Unblocks the next queued playground, if any.
 */
export function releaseSlot(): void {
  activeInits = Math.max(0, activeInits - 1);
  const next = waitQueue.shift();
  if (next) next();
}
